// Chamadas das duas páginas com dados da DRE (rotas do dre-engine).
// Contrato: dre-sistema/docs/dre-setores.md (se divergir, vale o doc).
// Só LÊ (menos o "Recalcular", que é a rota que a tela de Resultado por contrato já usa).
import axios from 'axios';
import { API_URL } from '../../../config';
import { getToken } from '../../../utils/auth';

/** O que as páginas por tarefa recebem da página genérica (Plano de ação). */
/**
 * O que as duas páginas da DRE recebem. Tudo opcional: no Plano de ação vêm da tarefa;
 * como aba de Financeiro › DRE & Resultado vêm vazias (acesso pelo usePerms, hoje = data local).
 */
export interface PropsPagina {
  tarefa?: { id: number; titulo?: string; [k: string]: any };
  config?: Record<string, any>;
  /** A pessoa tem a permissão que a fonte exige (financeiro.dre.ver). Sem a prop, decide o usePerms. */
  podeVer?: boolean;
  /** Hoje, "AAAA-MM-DD". Sem a prop, a data local do navegador. */
  hoje?: string;
}

export const SEM_ACESSO = 'Você não tem acesso a esta página';

export class ErroDre extends Error {
  status: number | null;
  constructor(mensagem: string, status: number | null) { super(mensagem); this.status = status; }
}

export type Agrupar = 'setores' | 'frentes';
export type TipoGrupoCliente = 'cliente' | 'cnpj_raiz';
export type LinhaMargem = 'lucro_bruto' | 'resultado_op' | 'lucro_liq' | 'lucro_final';

// ── Faturamento ──────────────────────────────────────────────────────────
export interface FatMes {
  periodo: string; fonte?: string; parcial: boolean; total: number;
  por_setor: Record<string, number>; fora_da_dre: number;
}
export interface FatCliente {
  chave: string; cliente_id: number | string | null; nome: string; cnpj: string | null;
  identificado: boolean; como?: string; membros?: { nome: string; cnpj?: string | null; total?: number; [k: string]: any }[]; posicao: number;
  total: number; pct: number; pct_acumulado: number;
  por_mes: Record<string, number>; por_setor: Record<string, number>;
  /** "AAAA/MM" → { setor: valor } (servidor desde 12/10/2026; ausente em servidor antigo). */
  por_mes_setor?: Record<string, Record<string, number>>;
  contratos?: any[];
}
export interface Faturamento12m {
  versao?: number; de: string; ate: string; agrupar: Agrupar; grupo: TipoGrupoCliente;
  setores: string[];
  meses: FatMes[];
  total: number;
  por_setor: { setor: string; valor: number; pct: number }[];
  clientes: FatCliente[];
  avisos: {
    sem_setor?: { valor: number; parcelas: number; manuais: number };
    fora_da_dre?: any[];
    nao_identificados?: { n: number; valor: number };
  };
  conferencia?: any;
  ms?: number;
}

// ── DRE por setor ────────────────────────────────────────────────────────────
export interface Fechamento {
  regra: 'atual' | 'antiga' | 'congelada' | 'sem_fechamento' | 'setores_desatualizados';
  execucao_id?: number | string | null; calculado_em?: string | null; ok?: boolean;
  /** 14/10/2026 — de onde veio o setor dos custos: a fotografia do fechamento, ou a ficha de hoje (meses congelados, em memória) */
  setores_fonte?: 'fotografia' | 'ficha_hoje' | null;
}
export type ValoresLinha = Record<string, number>; // setor → valor, mais "total"
export interface MesDre {
  periodo: string; parcial: boolean; fechamento: Fechamento;
  pesos?: Record<string, number>;
  valores: Record<string, ValoresLinha>;
  direto?: Record<string, number>;
  mao_obra_parada?: number;
  /** R$ do custo direto do mês por via de repartição (ausente em servidor antigo). */
  vias?: { item: number; contrato: number; sem_setor: number; sem_casamento: number };
  conferencia?: Record<string, number>;
  /** 15/10/2026 — por grupo da DRE, as categorias (linha_dre) que o compõem, repartidas por setor/frente (ausente em servidor antigo). */
  categorias?: Record<string, Record<string, ValoresLinha>>;
}
export interface Acumulado {
  valores: Record<string, ValoresLinha>;
  margens?: Partial<Record<LinhaMargem, Record<string, number | null>>>;
}
export interface DreSetores {
  versao?: number; de: string; ate: string; agrupar: Agrupar; tax_source?: string;
  setores: string[];
  linhas: { chave: string; tipo: string; rotulo?: string }[];
  meses: MesDre[];
  acumulado: Acumulado;
  pendencias: Record<string, any>;
  ms?: number;
}

function desembrulhar(dado: any): any {
  if (dado && typeof dado === 'object' && typeof dado.body === 'string') {
    try { return JSON.parse(dado.body); } catch { /* segue com o original */ }
  }
  return dado;
}

function falha(e: any): ErroDre {
  const status: number | null = e?.response?.status ?? null;
  if (status === 403) return new ErroDre(SEM_ACESSO, 403);
  const d = desembrulhar(e?.response?.data);
  const msg = d?.erro || d?.error || d?.message;
  if (msg) return new ErroDre(String(msg), status);
  if (status == null) return new ErroDre('Sem resposta do servidor. Tente de novo em instantes.', null);
  return new ErroDre(`Erro ${status} ao buscar os dados.`, status);
}

async function req<T>(metodo: 'get' | 'post', caminho: string, params?: Record<string, any>, corpo?: any, timeout = 60000): Promise<T> {
  const token = await getToken();
  try {
    const r = await axios.request({
      method: metodo, url: `${API_URL}${caminho}`, params, data: corpo, timeout,
      headers: { Authorization: token },
    });
    const d = desembrulhar(r.data);
    if (d?.erro) throw new ErroDre(String(d.erro), r.status);
    return d as T;
  } catch (e: any) {
    if (e instanceof ErroDre) throw e;
    throw falha(e);
  }
}

export interface PedidoFaturamento {
  /** "AAAA/MM"; janela de no máximo 36 meses. Vazios = últimos 12 meses fechados (padrão do servidor). */
  de?: string;
  ate?: string;
  agrupar: Agrupar;
  grupo: TipoGrupoCliente;
}
export function buscarFaturamento12m(p: PedidoFaturamento): Promise<Faturamento12m> {
  const params: Record<string, string> = { agrupar: p.agrupar, grupo: p.grupo };
  if (p.de) params.de = p.de;
  if (p.ate) params.ate = p.ate;
  return req<Faturamento12m>('get', '/financeiro/faturamento-12m', params);
}

export interface PedidoDreSetores {
  de?: string; ate?: string; agrupar: Agrupar;
}
export function buscarDreSetores(p: PedidoDreSetores): Promise<DreSetores> {
  const params: Record<string, string> = { agrupar: p.agrupar };
  if (p.de) params.de = p.de;
  if (p.ate) params.ate = p.ate;
  return req<DreSetores>('get', '/financeiro/dre-setores', params);
}

/** Refaz o fechamento do mês (mesma rota da tela "Por contrato"). */
export function recalcularFechamento(competencia: string): Promise<any> {
  return req('post', '/financeiro/fechamento-cc/recalcular', undefined, { competencia }, 120000);
}

/** Aceite "segue o contrato" (14/10/2026) para custo cujo setor o contrato não vende — Margem › O que conferir. */
export type EscopoAceite = 'contrato' | 'pessoa' | 'bem' | 'kit';
export function aceitarSetorCusto(p: { contrato_id: string; escopo: EscopoAceite; ref?: string | null; observacao?: string }): Promise<{ ok: boolean; id: number | null; ja_existia: boolean }> {
  return req('post', '/financeiro/setores-custo/aceites', undefined, p);
}
export function desfazerAceiteSetorCusto(id: number): Promise<{ ok: boolean }> {
  return req('post', '/financeiro/setores-custo/aceites/desfazer', undefined, { id });
}

/** Avaliação de UM contrato quanto ao rateio por setor (14/10/2026) — bloco "Na Margem por setor" do cartão Áreas e CNAE. */
export interface ItemAvaliacaoSetor {
  tipo: 'pessoa' | 'lancamento' | 'bem' | 'nota' | 'outro';
  ref: string; nome: string; contrato_id: string; numero: string; linha: string;
  setores_item: Record<string, number> | null;
  mix: Record<string, number> | null;
  valor: number; meses: string[];
  aceito?: { id: number; escopo: string; por: string; em: string };
}
export interface AvaliacaoContratoSetores {
  de: string; ate: string;
  contrato: { id: string; raiz: string; numero: string; titulo: string; padrao: Record<string, number> | null; rotulo: string | null };
  vale: { mix: Record<string, number>; origem: 'parcelas' | 'faturamento_antigo' | 'padrao_contrato' | 'rotulo_contrato' | 'sem_setor' };
  custos: {
    total: number; por_setor: Record<string, number>;
    vias: { item: number; contrato: number; sem_setor: number; sem_casamento: number }; itens: number;
  };
  pendencias: { custos_sem_setor: ItemAvaliacaoSetor[]; custos_sem_casamento: ItemAvaliacaoSetor[] };
  aceites: { id: number; escopo: EscopoAceite; ref: string | null; nome: string; por: string; em: string }[];
  parcelas: { total: number; sem_setor: number; valor_sem_setor: number };
  pode_aceitar: boolean;
}
export function avaliarContratoSetores(contratoId: string, p?: { de?: string; ate?: string }): Promise<AvaliacaoContratoSetores> {
  const params: Record<string, string> = {};
  if (p?.de) params.de = p.de;
  if (p?.ate) params.ate = p.ate;
  return req<AvaliacaoContratoSetores>('get', `/financeiro/setores-custo/contrato/${contratoId}`, params);
}

// ── Auditoria: como foi calculada uma célula (categoria × setor) ────────────
export interface ItemDetalheSetor {
  competencia: string;
  contrato_id: string | null; contrato: string | null; titulo: string | null;
  origem: string;
  quem: { tipo: 'pessoa' | 'lancamento' | 'bem' | 'nota' | 'outro'; nome: string | null };
  valor: number;
  via: 'item' | 'contrato' | 'sem_setor' | 'sem_casamento';
  peso: number;
  valor_setor: number;
  setores_item: Record<string, number> | null;
  mix: Record<string, number> | null;
  setores_origem: string | null;
}
export interface RateioDetalheSetor {
  competencia: string; dre: number; diretos: number; restante: number; peso: number;
  peso_origem: 'mes' | 'janela' | 'sem_receita'; valor_setor: number;
}
export interface DetalheSetor {
  linha: string; categoria: string; setor: string; de: string; ate: string; agrupar: Agrupar;
  total: number; soma_itens: number; soma_rateio: number; n_itens: number; truncado: boolean;
  itens: ItemDetalheSetor[]; rateio: RateioDetalheSetor[];
}
export async function buscarDetalheSetor(params: {
  de: string; ate: string; agrupar: Agrupar; tax_source?: string; linha: string; categoria: string; setor: string;
}): Promise<DetalheSetor> {
  const p: Record<string, string> = { ...params } as any;
  if (!p.tax_source) delete p.tax_source;
  return req<DetalheSetor>('get', '/financeiro/dre-setores/detalhe', p, undefined, 60000);
}
